import * as XLSXStyle from 'xlsx-js-style';
import {
  applyRelacionVentasColumnLabelsFromApi,
  buildRelacionFootCells,
  buildRelacionSegments,
  extractRelacionApiFootTotals,
  formatRelacionCell,
  mergeRelacionTotalsSlice,
  mergeRelacionVentasEncabezado,
  resolveRelacionVentasColumns,
  rowToRelacionNorm,
  sortRelacionRowsForDisplay,
  sumRelacionTotals,
  withRelacionVentasPeriodoLine,
} from './relacionVentasReportModel';
import { formatPeriodoDescuentosLine } from './descuentosCortesiasReportModel';
import { appendFiltrosSucursalesCajasExcel } from './reporteFiltrosEncabezadoExport';

function appendRelacionEncabezadoExcel(aoa, encMerged, form, sucursalNombre = '') {
  // Sucursal encima del bloque corporativo (igual que popularidad)
  const sn = String(sucursalNombre || '').trim();
  const ncUp = String(encMerged.nombreComercial || '').toUpperCase();
  if (sn && !ncUp.includes(sn.toUpperCase())) {
    aoa.push([sn.toUpperCase()]);
  }
  let titleRowIndex = -1;
  const up = (s) => String(s || '').trim().toUpperCase();
  if (
    encMerged.bloqueTexto &&
    String(encMerged.bloqueTexto).trim() &&
    !encMerged.nombreComercial &&
    !encMerged.reporteTitulo
  ) {
    for (const line of String(encMerged.bloqueTexto).split('\n')) {
      const t = line.trim();
      if (t) aoa.push([t]);
    }
    aoa.push([]);
    return { titleRowIndex: -1 };
  }
  if (encMerged.nombreComercial) {
    aoa.push([up(encMerged.nombreComercial)]);
  }
  if (encMerged.domicilioFiscal && String(encMerged.domicilioFiscal).trim()) {
    aoa.push([String(encMerged.domicilioFiscal).trim()]);
  }
  if (encMerged.rfc) {
    aoa.push([up(`RFC: ${encMerged.rfc}`)]);
  }
  if (encMerged.reporteTitulo) {
    titleRowIndex = aoa.length;
    aoa.push([up(encMerged.reporteTitulo)]);
  }
  if (encMerged.periodoTexto) {
    aoa.push([String(encMerged.periodoTexto)]);
  } else {
    aoa.push([formatPeriodoDescuentosLine(form.fechaInicio, form.fechaFin)]);
  }
  appendFiltrosSucursalesCajasExcel(aoa, encMerged, form);
  if (encMerged.turnosTexto) {
    aoa.push([`Turnos: ${encMerged.turnosTexto}`]);
  } else {
    aoa.push([`De: ${form.turnoInicio ?? '—'} a ${form.turnoFin ?? '—'}`]);
  }
  aoa.push([]);
  return { titleRowIndex };
}

/**
 * @param {object[]} rawRows
 * @param {object} options
 */
export function buildRelacionVentasExcelPayload(rawRows, options = {}) {
  const {
    fechaInicio = '',
    fechaFin = '',
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    encabezado: encApi,
    encabezadoFallback,
    sucursalNombre = '',
    reporteTitulo,
    apiTotales = null,
    columnas = null,
    ordenarMesa = false,
    agruparTurno = false,
  } = options;

  const form = { fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin };
  const encBase = mergeRelacionVentasEncabezado(encApi, encabezadoFallback);
  const encMerged = withRelacionVentasPeriodoLine(
    reporteTitulo ? { ...encBase, reporteTitulo } : encBase,
    fechaInicio,
    fechaFin
  );

  const aoa = [];
  const { titleRowIndex } = appendRelacionEncabezadoExcel(aoa, encMerged, form, sucursalNombre);

  const list = Array.isArray(rawRows) ? rawRows : [];
  const resolvedBase = resolveRelacionVentasColumns(list.length > 0 ? list[0] : null);
  const resolved = applyRelacionVentasColumnLabelsFromApi(resolvedBase, columnas);
  const nCol = resolved.length;

  if (titleRowIndex >= 0 && aoa[titleRowIndex]) {
    const t = aoa[titleRowIndex][0];
    aoa[titleRowIndex] = resolved.map((_, i) => (i === 0 ? t : ''));
  }

  const encRows = aoa.length;
  const sorted = sortRelacionRowsForDisplay(list, resolved, ordenarMesa);
  const segments = buildRelacionSegments(sorted, agruparTurno);
  const normRows = sorted.map((r) => rowToRelacionNorm(r, resolved));
  const summed = sumRelacionTotals(normRows, resolved);
  const { venta: apiVenta, cortesia: apiCortesia } = extractRelacionApiFootTotals(apiTotales);
  const totalsVenta = mergeRelacionTotalsSlice(summed, apiVenta, resolved);
  const zeroTotals = Object.fromEntries(resolved.filter((c) => c.total).map((c) => [c.id, 0]));
  const totalsCortesia = mergeRelacionTotalsSlice(zeroTotals, apiCortesia, resolved);

  aoa.push(resolved.map((c) => c.label));
  const colHeaderRow = encRows;
  const dataStartRow = encRows + 1;

  /** @type {{ s: { r: number, c: number }, e: { r: number, c: number }, turn?: boolean }[]} */
  const groupMerges = [];

  for (const seg of segments) {
    const r = aoa.length;
    if (seg.kind === 'turnGroup' || seg.kind === 'group') {
      aoa.push([
        seg.kind === 'turnGroup' ? String(seg.label) : String(seg.label).toUpperCase(),
        ...Array(Math.max(nCol - 1, 0)).fill(''),
      ]);
      if (nCol > 0) {
        groupMerges.push({
          s: { r, c: 0 },
          e: { r, c: Math.max(nCol - 1, 0) },
          turn: seg.kind === 'turnGroup',
        });
      }
    } else {
      const norm = rowToRelacionNorm(seg.raw, resolved);
      aoa.push(resolved.map((c) => formatRelacionCell(c, norm[c.id])));
    }
  }

  aoa.push(buildRelacionFootCells(resolved, 'TOTAL VENTA', totalsVenta));
  const footVentaRow = aoa.length - 1;
  aoa.push(buildRelacionFootCells(resolved, 'TOTAL CORTESIA', totalsCortesia));
  const footCortesiaRow = aoa.length - 1;

  return {
    aoa,
    resolved,
    encRows,
    colHeaderRow,
    dataStartRow,
    footVentaRow,
    footCortesiaRow,
    titleRowIndex,
    groupMerges,
  };
}

function styleRelacionSheet(ws, meta) {
  const {
    resolved,
    encRows,
    colHeaderRow,
    dataStartRow,
    footVentaRow,
    footCortesiaRow,
    titleRowIndex,
    groupMerges,
  } = meta;
  if (!resolved.length || colHeaderRow < 0) return;

  const nDataCol = resolved.length;
  const groupRowSet = new Set((groupMerges || []).map((m) => m.s.r));
  if (!ws['!merges']) ws['!merges'] = [];
  for (const m of groupMerges || []) {
    ws['!merges'].push(m);
  }

  if (titleRowIndex >= 0 && nDataCol > 0) {
    ws['!merges'].push({
      s: { r: titleRowIndex, c: 0 },
      e: { r: titleRowIndex, c: Math.max(nDataCol - 1, 0) },
    });
    for (let c = 0; c < nDataCol; c++) {
      const addr = XLSXStyle.utils.encode_cell({ r: titleRowIndex, c });
      const cell = ws[addr];
      if (cell) {
        cell.s = {
          ...(cell.s || {}),
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          fill: { fgColor: { rgb: 'FFBFDBFE' } },
        };
      }
    }
  }

  const encEnd = encRows - 1;
  for (let r = 0; r <= encEnd; r++) {
    if (r === titleRowIndex) continue;
    if (nDataCol > 1) {
      ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: nDataCol - 1 } });
    }
    const addr = XLSXStyle.utils.encode_cell({ r, c: 0 });
    const cell = ws[addr];
    if (cell) {
      cell.s = {
        ...(cell.s || {}),
        alignment: { horizontal: 'center', wrapText: true },
        font: { ...((cell.s && cell.s.font) || {}), color: { rgb: 'FF0F172A' } },
      };
    }
  }

  for (const mr of groupMerges || []) {
    const addr = XLSXStyle.utils.encode_cell({ r: mr.s.r, c: 0 });
    const cell = ws[addr];
    if (cell) {
      cell.s = {
        ...(cell.s || {}),
        font: {
          bold: true,
          color: { rgb: mr.turn ? 'FFE0F2FE' : 'FF0F172A' },
        },
        alignment: { horizontal: 'left', vertical: 'center' },
        fill: { fgColor: { rgb: mr.turn ? 'FF0F2F59' : 'FFF1F5F9' } },
      };
    }
  }

  for (let c = 0; c < nDataCol; c++) {
    const align = resolved[c].align === 'right' ? 'right' : 'left';
    const th = XLSXStyle.utils.encode_cell({ r: colHeaderRow, c });
    const tc = ws[th];
    if (tc) {
      tc.s = {
        ...(tc.s || {}),
        font: { bold: true, color: { rgb: 'FFFFFFFF' } },
        alignment: { horizontal: align },
        fill: { fgColor: { rgb: 'FF0F2F59' } },
        border: { bottom: { style: 'thin', color: { rgb: 'FF000000' } } },
      };
    }

    for (let r = dataStartRow; r <= footCortesiaRow; r++) {
      if (r === colHeaderRow) continue;
      if (groupRowSet.has(r)) continue;
      const addr = XLSXStyle.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;
      const base = {
        ...(cell.s || {}),
        alignment: { horizontal: align },
        font: { color: { rgb: 'FF0F172A' } },
      };
      if (r === footVentaRow || r === footCortesiaRow) {
        const isTotal = resolved[c].total || c === 0;
        cell.s = {
          ...base,
          font: { bold: true, color: { rgb: 'FF000000' } },
          border: isTotal
            ? {
                top: { style: 'double', color: { rgb: 'FF000000' } },
                bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              }
            : { bottom: { style: 'thin', color: { rgb: 'FF000000' } } },
        };
      } else {
        cell.s = base;
      }
    }
  }
}

export function downloadRelacionVentasExcelFile(rawRows, options, fileName, sheetName) {
  const payload = buildRelacionVentasExcelPayload(rawRows, options);
  const {
    aoa,
    resolved,
    encRows,
    colHeaderRow,
    dataStartRow,
    footVentaRow,
    footCortesiaRow,
    titleRowIndex,
    groupMerges,
  } = payload;

  const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
  if (resolved.length) {
    styleRelacionSheet(ws, {
      resolved,
      encRows,
      colHeaderRow,
      dataStartRow,
      footVentaRow,
      footCortesiaRow,
      titleRowIndex,
      groupMerges,
    });
  }

  const colCount = Math.max(1, aoa.reduce((m, row) => Math.max(m, row.length), 0));
  const wch = [12, 8, 8, 10, 14, 14, 12, 12, 14, 12, 8, 28, 10, 10];
  ws['!cols'] = Array.from({ length: colCount }, (_, i) => ({
    wch: wch[i] ?? 12,
  }));

  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
  XLSXStyle.writeFile(wb, fileName);
}
